// Game configuration and colors

import { CANVAS_WIDTH, PIN_SPACING_X_BASE, PIN_SPACING_Y_BASE, PIN_START, SPECIAL_PIN_SPACING_X_BASE, SPECIAL_PIN_SPACING_Y_BASE } from "./constants";

// Colors
export const COLORS = {
  background: '#132129',
  red: '#fa013e',
  yellow: '#fcc002',
  darkRed: '#920007',
  darkYellow: '#9b7800',
  white: '#ffffff',
  gray: '#808080',
  darkGray: '#404040',
  black: '#000000',
  green: '#20fa20',
  hoverGreen: '#18c818',
  darkGreen: '#109610',
  pin: '#808080', // 일반 핀 색상
  specialPin: '#ff6b6b', // 특수 핀 색상 (빨간색)
  specialPinGlow: '#ff9999' // 특수 핀 글로우 색상
} as const;

// Pin layout configuration
export const PIN_LAYOUT = [
       [1,1,1,1,1],
        [1,1,1,1],
       [1,1,0,1,1],
      [1,1,0,0,1,1],
     [1,1,0,0,0,1,1],
    [1,1,1,1,1,1,1,1],
   [1,1,1,1,1,1,1,1,1],
  [1,1,1,1,1,1,1,1,1,1],
   [1,1,1,1,1,1,1,1,1],
   [1,1,1,1,1,1,1,1,1], // hidden
] as const;

// Special pin layout
export const SPECIAL_PIN_LAYOUT = [
  [1,1],
  [1]
] as const;

// Special pin configuration
export const SPECIAL_PIN_CONFIG = {
  offsetX: 0,  // Horizontal offset in PIN_SPACING units
  offsetY: 0,  // Vertical offset in PIN_SPACING units
  startRow: 3, // Starting row index (0-based) for special pin layout
} as const;

// Game states
export const GAME_STATES = {
  WAITING: 'waiting',
  PLAYING: 'playing',
  BONUS: 'bonus',
  LEVELUP: 'levelup',
  GAMEOVER: 'gameover',
  MAXLEVEL: 'maxlevel'
} as const;

export type GameState = typeof GAME_STATES[keyof typeof GAME_STATES];

const initializePins = () => {
  const layout = PIN_LAYOUT
  const layoutDimensions = {
    rowCount: layout.length,
    maxCols: layout.length > 0 ? layout[0].length : 0
  }

  const newPins: Pin[] = [];
  const centerX = CANVAS_WIDTH / 2;
  const colSpacing = PIN_SPACING_X_BASE;

  for (let rowIndex = 0; rowIndex < layoutDimensions.rowCount; rowIndex++) {
    const rowData = layout[rowIndex];
    if (!rowData) {
      continue;
    }

    const y = (rowIndex + 1) * PIN_SPACING_Y_BASE + PIN_START;
    const rowStartX = centerX - ((rowData.length - 1) / 2) * colSpacing;
    const isLastRow = rowIndex === layoutDimensions.rowCount - 1

    for (let colIndex = 0; colIndex < rowData.length; colIndex++) {
      if (rowData[colIndex] === 1) {
        const x = rowStartX + colIndex * colSpacing;
        if (isLastRow) {
          newPins.push({ x, y: y - 5, visible: false });
        } else {
          newPins.push({ x, y, visible: true });
        }
      }
    }
  }

  const specialColSpacing = SPECIAL_PIN_SPACING_X_BASE;
  const specialCenterX = centerX + SPECIAL_PIN_CONFIG.offsetX * PIN_SPACING_X_BASE;
  const specialStartY = (SPECIAL_PIN_CONFIG.startRow + 1) * PIN_SPACING_Y_BASE + PIN_START + SPECIAL_PIN_CONFIG.offsetY * PIN_SPACING_Y_BASE;

  for (let rowIndex = 0; rowIndex < SPECIAL_PIN_LAYOUT.length; rowIndex++) {
    const rowData = SPECIAL_PIN_LAYOUT[rowIndex];
    const actualRowIndex = SPECIAL_PIN_CONFIG.startRow + rowIndex;

    if (actualRowIndex < layoutDimensions.rowCount) {
      const y = specialStartY + rowIndex * SPECIAL_PIN_SPACING_Y_BASE;
      const rowStartX = specialCenterX - ((rowData.length - 1) / 2) * specialColSpacing;

      for (let colIndex = 0; colIndex < rowData.length; colIndex++) {
        if (rowData[colIndex] === 1) {
          const x = rowStartX + colIndex * specialColSpacing;
          newPins.push({ x, y, isSpecial: true, visible: true });
        }
      }
    }
  }

  return {
    pins: newPins, 
    layoutDimensions,
  }
}

export const INITIALIZED_PINS = initializePins()

export type Ball = {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  randomSeed: number;
  collisionCooldown: number;
  currentSpecialHits: number;
  stuckCount: number;
  /**
   * Sideways acceleration applied every frame — a curve ball. Undefined for ordinary balls, so
   * v1/v3 are unaffected. Used by the bonus round to push a ball toward the high-paying edges
   * through the physics rather than by rewriting its outcome.
   */
  spin?: number;
};

export type Pin = {
  x: number;
  y: number;
  isSpecial?: boolean;
  visible: boolean;
};
